
/**
 * Profile, default address, marketing preferences and the deletion request.
 * T-P3.7.
 *
 * The deletion request is a tertiary link rather than a red button: making a
 * destructive action visually loud makes it easier to hit by accident, which is
 * the opposite of what it needs.
 */

import { useNavigate, useRouter } from '@tanstack/react-router';
import { resetIdentity } from '@/query-client';
import { useState } from 'react';

import { Button, Callout, Field, inputClass } from '@/components/ui';

const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

export function AccountForm({ email, name }: { email: string; name: string }) {
  const navigate = useNavigate();
  const router = useRouter();
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteAck, setDeleteAck] = useState(false);
  const [requested, setRequested] = useState(false);

  async function signOut() {
    await fetch('/api/session', { method: 'DELETE' });
    // Same reasoning as sign-in: the cache still holds this person's orders,
    // quotes and admin role until it is dropped.
    resetIdentity();
    await router.invalidate();
    await navigate({ to: '/' });
  }

  return (
    <div className="max-w-[520px] space-y-10">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSaved(true);
          window.setTimeout(() => setSaved(false), 2600);
        }}
        className="space-y-6"
      >
        <h2 className="text-lg font-bold tracking-[-0.02em]">Profile</h2>

        {saved && <Callout tone="ok" title="Profile saved" />}

        <Field label="Full name" htmlFor="full_name">
          <input
            id="full_name"
            name="full_name"
            defaultValue={name}
            autoComplete="name"
            className={inputClass}
          />
        </Field>

        <Field label="Company" htmlFor="company" optional>
          <input
            id="company"
            name="company"
            autoComplete="organization"
            className={inputClass}
          />
        </Field>

        <Field label="Phone" htmlFor="phone" optional>
          <input
            id="phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            className={`${inputClass} max-w-[220px]`}
          />
        </Field>

        <h3 className="pt-2 text-[15px] font-bold">Default address</h3>

        <Field label="Street address" htmlFor="line1">
          <input id="line1" name="line1" autoComplete="address-line1" className={inputClass} />
        </Field>

        <Field label="Suburb" htmlFor="suburb">
          <input
            id="suburb"
            name="suburb"
            autoComplete="address-level2"
            className={`${inputClass} max-w-[340px]`}
          />
        </Field>

        <div className="flex flex-wrap gap-4">
          <Field label="State" htmlFor="state">
            <select id="state" name="state" defaultValue="NSW" className={`${inputClass} w-[130px]`}>
              {AU_STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Postcode" htmlFor="postcode">
            <input
              id="postcode"
              name="postcode"
              inputMode="numeric"
              maxLength={4}
              autoComplete="postal-code"
              className={`${inputClass} w-[120px]`}
            />
          </Field>
        </div>

        <h3 className="pt-2 text-[15px] font-bold">Notifications</h3>
        <label className="flex items-start gap-3 text-[15px]">
          <input
            type="checkbox"
            name="marketing_push_opt_in"
            className="mt-1 size-5 accent-[var(--color-brand-strong)]"
          />
          {/* Positive phrasing: the label says what happens when it is ticked. */}
          <span>
            Send me new offers and order updates in the app.
            <span className="mt-0.5 block text-[13px] text-muted">
              Order and proof notifications are sent either way.
            </span>
          </span>
        </label>

        <Button type="submit">Save changes</Button>
      </form>

      <div className="border-t border-line pt-8">
        <h2 className="text-lg font-bold tracking-[-0.02em]">Account</h2>
        <div className="mt-4 flex flex-wrap items-center gap-5">
          <Button weight="secondary" onClick={signOut}>
            Sign out
          </Button>

          {!confirmDelete && !requested && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="py-2 text-sm font-semibold text-muted underline underline-offset-4 hover:text-ink"
            >
              Request account deletion
            </button>
          )}
        </div>

        {requested && (
          <div className="mt-5">
            <Callout tone="ok" title="Deletion request received">
              We will confirm by email within five business days. Orders we are legally
              required to keep are retained; everything else goes.
            </Callout>
          </div>
        )}

        {confirmDelete && !requested && (
          <div className="mt-5 rounded-[10px] border border-danger/40 bg-danger-wash p-5">
            <p className="font-bold">Delete your account</p>
            <p className="mt-1.5 text-[15px]">
              This removes your profile, addresses and saved preferences. Orders we are
              legally required to keep are retained.
            </p>
            <label className="mt-4 flex items-start gap-3 text-[15px]">
              <input
                type="checkbox"
                checked={deleteAck}
                onChange={(e) => setDeleteAck(e.target.checked)}
                className="mt-1 size-5 accent-[var(--color-danger)]"
              />
              <span>I understand this cannot be undone.</span>
            </label>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button weight="secondary" onClick={() => setConfirmDelete(false)}>
                Keep my account
              </Button>
              <Button
                weight="danger"
                onClick={() => {
                  if (!deleteAck) return;
                  setRequested(true);
                  setConfirmDelete(false);
                }}
              >
                Request deletion
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
